import React, {useEffect, useMemo, useState} from 'react';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import CustomIcon from "../CustomIcon/CustomIcon";
import greenCircle from "../../Images/green-circle.png";
import redCircle from "../../Images/red-circle.png";
import greyCircle from "../../Images/grey-circle.png";
import {WINT_SITES_ROUTE} from "../../Constants/constants";
import {CONNECTED, DISCONNECTED, INTERRUPTED, UNKNOWN} from "../../Pages/ControlUnits/Types";
import MaterialUiRemoteTable from "../Table/MaterialUiRemoteTable";
import {ControlUnitService} from "../../Pages/ControlUnits/ControlUnitService";
import {MULTI_SELECT, SELECT, TEXT} from "../../Pages/Errors/Constants/Constants";

export default function ControlUnitsTable({controlUnitFormatter}) {
    const getInitialFilters = () => {
        const savedFilters = sessionStorage.getItem('controlUnitsTableFilters');
        return savedFilters ? JSON.parse(savedFilters) : [
            {
                id: 'lifecyclePhase',
                value: ['In-Service', 'Pre-Service']
            }
        ];
    };

    const [columnFilters, setColumnFilters] = useState(getInitialFilters());

    const getInitialSorting = () => {
        const savedSorting = sessionStorage.getItem('controlUnitsTableSorting');
        return savedSorting ? JSON.parse(savedSorting) : [];
    };

    const [sorting, setSorting] = useState(getInitialSorting());

    const getInitialColumnVisibility = () => {
        const savedColumnVisibility = sessionStorage.getItem('controlUnitsTableColumnVisibility');
        return savedColumnVisibility ? JSON.parse(savedColumnVisibility) : {};
    };

    const [columnVisibility, setColumnVisibility] = useState(getInitialColumnVisibility());
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    const initialPage = pageParam ? parseInt(pageParam) : 0;

    const getInitialPageSize = () => {
        const savedPageSize = sessionStorage.getItem('controlUnitsTablePageSize');
        return savedPageSize ? parseInt(savedPageSize) : 15;
    };

    const [pagination, setPagination] = useState({pageIndex: initialPage, pageSize: getInitialPageSize()});
    const controlUnitService = new ControlUnitService(process.env.REACT_APP_SUPPORT_DASHBOARD_CONTROL_UNITS_URL);
    const [isErrorsLoaded, setIsErrorsLoaded] = useState(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const pageParam = urlParams.get('page');
        if (pageParam) {
            setPagination(prev => ({...prev, pageIndex: parseInt(pageParam)}));
        }
    }, [window.location.search]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const pageParam = urlParams.get('page');
        if (pageParam && parseInt(pageParam) === pagination.pageIndex) {
            return;
        }

        urlParams.set('page', pagination.pageIndex.toString());
        const newUrl = window.location.pathname + '?' + urlParams.toString();
        window.history.replaceState(null, '', newUrl);
    }, [pagination.pageIndex]);

    useEffect(() => {
        sessionStorage.setItem('controlUnitsTableFilters', JSON.stringify(columnFilters));
    }, [columnFilters]);

    useEffect(() => {
        sessionStorage.setItem('controlUnitsTablePageSize', pagination.pageSize.toString());
    }, [pagination.pageSize]);

    useEffect(() => {
        sessionStorage.setItem('controlUnitsTableSorting', JSON.stringify(sorting));
    }, [sorting]);

    useEffect(() => {
        sessionStorage.setItem('controlUnitsTableColumnVisibility', JSON.stringify(columnVisibility));
    }, [columnVisibility]);

    const statusFilterOptions = [
        {value: 'CONNECTED', label: 'Connected'},
        {value: 'DISCONNECTED', label: 'Disconnected'}
    ];

    const loggingFilterOptions = [
        {value: 'FATAL', label: 'Fatal'},
        {value: 'ERROR', label: 'Error'},
        {value: 'WARNING', label: 'Warning'},
        {value: 'INFO', label: 'Info'},
        {value: 'DEBUG', label: 'Debug'}
    ];

    const communicationChannelFilterOptions = [
        {value: 'ETHERNET', label: 'Ethernet'},
        {value: 'WIFI', label: 'WiFi'},
        {value: 'CELLULAR', label: 'Cellular'},
        {value: 'CELLULAR_SMS_MODE', label: 'Cellular SMS Mode'},
        {value: 'UNKNOWN', label: 'Unknown'},
    ];

    const lifecyclePhaseFilterOptions = [
        {value: 'ENROLLED', label: 'Enrolled'},
        {value: 'PRE_SERVICE', label: 'Pre-Service'},
        {value: 'IN_SERVICE', label: 'In-Service'},
        {value: 'INTERNAL_USE', label: 'Internal Use'},
        {value: 'DECOMMISSIONED', label: 'Decommissioned'},
        {value: 'HARDWARE_DONOR', label: 'Hardware Donor'},
    ];

    const columns = useMemo(() => [
        {
            accessorKey: 'connectionState',
            header: 'Connectivity',
            size: 80,
            filterVariant: SELECT,
            filterSelectOptions: statusFilterOptions.map(option => option.label),
            enumArray: statusFilterOptions,
            filterFn: () => null,
            Cell: ({cell}) => {
                const value = cell.getValue();
                let statusIcon;
                let status;
                switch (value) {
                    case CONNECTED:
                        statusIcon = (
                            <CustomIcon
                                className="ml-2 mb-1"
                                width={20}
                                height={20}
                                src={greenCircle}
                                alt="greenCircle"
                            />
                        );
                        status = CONNECTED.charAt(0) + CONNECTED.toLowerCase().substring(1);
                        break;
                    case DISCONNECTED:
                    case INTERRUPTED:
                        statusIcon = (
                            <CustomIcon
                                className="ml-2 mb-1"
                                width={20}
                                height={20}
                                src={redCircle}
                                alt="redCircle"
                            />
                        );
                        status = DISCONNECTED.charAt(0) + DISCONNECTED.toLowerCase().substring(1);
                        break;
                    case UNKNOWN:
                    default:
                        statusIcon = (
                            <CustomIcon
                                className="ml-2 mb-1"
                                width={20}
                                height={20}
                                src={greyCircle}
                                alt="greyCircle"
                            />
                        );
                        status = UNKNOWN.charAt(0) + UNKNOWN.toLowerCase().substring(1);
                }
                return (
                    <OverlayTrigger overlay={<Tooltip>{status}</Tooltip>}>
                        <span className="justify-content-center d-flex" tabIndex="0">{statusIcon}</span>
                    </OverlayTrigger>
                );
            }
        },
        {
            accessorKey: 'name',
            header: 'Name',
            filterVariant: TEXT,
            filterFn: () => null,
            size: 300,
            Cell: ({cell, row}) => {
                const value = cell.getValue();
                const [showTooltip, setShowTooltip] = useState(false);
                const textRef = React.useRef(null);

                useEffect(() => {
                    if (textRef.current) {
                        const element = textRef.current;
                        setShowTooltip(element.scrollWidth > element.clientWidth);
                    }
                }, [value]);

                const content = (
                    <span ref={textRef} style={{display: 'block'}}>
                        {controlUnitFormatter(value, row.original, pagination.pageIndex)}
                    </span>
                );

                return (
                    <div style={{ 
                        maxWidth: '400px',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap' 
                    }}>
                        {showTooltip ? (
                            <OverlayTrigger placement="top" overlay={<Tooltip>{value}</Tooltip>}>
                                {content}
                            </OverlayTrigger>
                        ) : content}
                    </div>
                );
            }
        },
        {
            accessorKey: 'siteName',
            header: 'Site',
            filterVariant: TEXT,
            filterFn: () => null,
            size: 200,
            Cell: ({cell, row}) => {
                const value = cell.getValue();
                const href = `${process.env.REACT_APP_SUPPORT_DASHBOARD_WINT_AI_CONTROL_URL}/${WINT_SITES_ROUTE}/${row.original?.site?.id}`;
                const [showTooltip, setShowTooltip] = useState(false);
                const textRef = React.useRef(null);

                useEffect(() => {
                    if (textRef.current) {
                        const element = textRef.current;
                        setShowTooltip(element.scrollWidth > element.clientWidth);
                    }
                }, [value]);

                const content = (
                    <a ref={textRef} href={href} target="_blank" rel="noreferrer" style={{display: 'block'}}>
                        {value}
                    </a>
                );

                return (
                    <div style={{ 
                        maxWidth: '200px',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap' 
                    }}>
                        {showTooltip ? (
                            <OverlayTrigger placement="top" overlay={<Tooltip>{value}</Tooltip>}>
                                {content}
                            </OverlayTrigger>
                        ) : content}
                    </div>
                );
            }
        },
        {
            accessorKey: 'serialNumber',
            header: 'S/N',
            filterVariant: TEXT,
            filterFn: () => null,
            Cell: ({cell}) => cell.getValue()
        },
        {
            accessorKey: 'lifecyclePhase',
            header: 'Lifecycle Phase',
            filterVariant: MULTI_SELECT,
            filterSelectOptions: lifecyclePhaseFilterOptions.map(option => option.label),
            enumArray: lifecyclePhaseFilterOptions,
            filterFn: () => null,
            muiFilterTextFieldProps: {
                SelectProps: {
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (selected) => {
                        if (Array.isArray(selected) && selected.length > 0) {
                            return `${selected.length} selected`;
                        }
                        return <span style={{color: 'rgba(0, 0, 0, 0.38)'}}>Filter</span>;
                    }
                }
            },
            Cell: ({cell}) => {
                const found = lifecyclePhaseFilterOptions.find(obj => obj.value === cell.getValue());
                return found ? found.label : cell.getValue();
            }
        },
        {
            accessorKey: 'logLevel',
            header: 'Logging',
            filterVariant: SELECT,
            filterSelectOptions: loggingFilterOptions.map(option => option.label),
            enumArray: loggingFilterOptions,
            filterFn: () => null,
            Cell: ({cell}) => {
                const found = loggingFilterOptions.find(obj => obj.value === cell.getValue());
                return found ? found.label : cell.getValue();
            }
        },
        {
            accessorKey: 'softwareVersion',
            header: 'Software Version',
            enableColumnFilter: false,
            enableSorting: false
        },
        {
            accessorKey: 'numberOfVmas',
            header: '# VMA',
            enableColumnFilter: false,
            enableSorting: false,
            size: 50,
        },
        {
            accessorKey: 'communicationChannel',
            header: 'Comm Channel',
            enableColumnFilter: true,
            enableSorting: true,
            filterVariant: SELECT,
            filterSelectOptions: communicationChannelFilterOptions
                .filter(option => !['CELLULAR_SMS_MODE', 'UNKNOWN'].includes(option.value))
                .map(option => option.label),
            enumArray: communicationChannelFilterOptions,
            filterFn: () => null,
            Cell: ({cell}) => {
                const found = communicationChannelFilterOptions.find(obj => obj.value === cell.getValue());
                return found ? found.label : cell.getValue();
            },
        }
    ], [controlUnitFormatter, statusFilterOptions, loggingFilterOptions]);

    return (<>
            <MaterialUiRemoteTable
                columns={columns}
                getData={controlUnitService.getControlUnitsData.bind(controlUnitService)}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                sorting={sorting}
                pagination={pagination}
                setSorting={setSorting}
                setPagination={setPagination}
                additionalDataArguments={[]}
                setIsErrorsLoaded={setIsErrorsLoaded}
                renderAdditionalCustomToolBarActions={() => <></>}
                customRowPerPageOptions={[5, 10, 15, 20, 25, 30]}
                textFieldNames={[]}
                excelColumns={[]}
                columnVisibility={columnVisibility}
                setColumnVisibility={setColumnVisibility}
            />
        </>
    );
}
